
import { Box, Checkbox, FormControlLabel, Grid, Typography, createStyles, makeStyles } from "@material-ui/core";
import { useBorrowerPortalMediaQueries } from "@roc/feature-app-core";
import { formatDate } from "@roc/feature-utils";
import { Button, PDFPreview, Paper, PaperBorderType } from "@roc/ui";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import SignatureCanvas from 'react-signature-canvas';
import { AppraisalReassignmentLetterSigned } from "./appraisalReassignmentLetterSigned";
import { useDocumentStore } from "@roc/feature-documents";
import { AppraisalReassignmentLetterPDF } from "./appraisalReassignmentLetterPDF";
import { useStore } from "@roc/client-portal-shared/hooks";

export type AppraisalReassignmentLetterProps = {
  loanId?: any;
  propertyId?: any;
  onClose: () => void;
}

const useStyles = makeStyles(theme => createStyles({
  signGrid:{
    height: '90%',
  },
  mb3: {
    marginBottom: 24,
  },
  mb4: {
    marginBottom: 32,
  },
  mb2: {
    marginBottom: 16,
  },
  mb6: {
    marginBottom: 48,
  },
  sigCanvas: {
    width: '100%',
    height: 200,
    background: '#f7f7f7',
    border: '1px solid #aaa',
    borderRadius: 16,
  },
}))



export const AppraisalReassignmentLetter = observer((props:AppraisalReassignmentLetterProps)=>{
  const {loanId, propertyId, onClose} = props;
  const { documentStore: { documentFormStore }, } = useDocumentStore();
  const { appraisalReassignmentLetterStore: store } = documentFormStore;
  const { userStore } = useStore();

  useEffect(()=>{
    store.getAppraisalReassignmentLetterData(loanId, propertyId);
  },[])

  const classes = useStyles();
  const propertyAddress = store?.appraisalReassignmentLetterDetails?.propertyAddress;
  const lenderName = store?.appraisalReassignmentLetterDetails?.lenderAlias;
  const borrowerName = store?.appraisalReassignmentLetterDetails?.borrowerName;
  const TPOName = store?.appraisalReassignmentLetterDetails?.lenderName;

  const { isLargeScreen } = useBorrowerPortalMediaQueries();
  const [isAgreed, setAgreed] = useState(false);
  const [isSigned, setSigned] = useState(false);
  const sigCanvas = useRef(null);
  const [signature, setSignature] = useState(null);
  const signComplete = store?.isSigned;

  const handleSign = () => {
    const signatureURL = sigCanvas.current
      .getTrimmedCanvas()
      .toDataURL('image/png');
    setSignature(signatureURL);
    store.sign(loanId,propertyId,userStore.userInformation.userId,signatureURL);
  };

  const date = store.appraisalReassignmentLetterDetails?.signedDate ? new Date(store.appraisalReassignmentLetterDetails?.signedDate):null;

  const currentDate = date ? new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric"
  }).format(date):'Pending';

  return (
    <>
      <div id="screenshotParent" style={{display:'none'}}>
        <AppraisalReassignmentLetterPDF
          store={store}
          signature={signature}
        />
      </div>
      {signComplete ?
      <>
        <AppraisalReassignmentLetterSigned store={store}/>
      </>
      :
      <>
        <Typography>
          Take a moment to read the letter carefully and sign it if you agree with its contents.
        </Typography>
        <Grid item xs={12} className={classes.signGrid}>
          <Grid container item xs={12}>
            <Grid item xs={12} sm={12}>
              <Box mb={3}>
                <Paper variant="outlined"
                  style={{
                    maxHeight: isLargeScreen ? 300 : 150,
                    overflowY: 'auto',
                    padding: 24,
                    borderColor: '#ccc',
                  }}
                  borderType={PaperBorderType.SOLID_BORDER}
                >
                  <Typography className={classes.mb3}>
                    {currentDate}
                  </Typography>
                  <Typography className={classes.mb4}>
                    To whom this may concern:
                  </Typography>
                  <Box className={classes.mb2}>
                    <Typography>
                      {propertyAddress}
                    </Typography>
                    <Typography>
                      Borrower: {borrowerName}
                    </Typography>
                  </Box>
                  <Typography className={classes.mb2}>
                    {lenderName} hereby transfers releases the attached appraisals for the properties noted above to:
                  </Typography>
                  <Typography className={classes.mb2}>
                    <strong>Loan Funder LLC, ISAOA/ATIMA</strong>
                  </Typography>
                  <Typography className={classes.mb3}>
                    {lenderName} certifies that this appraisal was prepared in accordance with meets all requirements of the Appraisal Independent Requirements (AIR), is USPAP compliant.
                  </Typography>
                  <Typography className={classes.mb4}>
                    The transferring lender, appraisal management company (AMC), appraiser selection, ordering policy and process, and the appraiser complied with all AIR, Dodd Frank, FIRREA, and Consumer Protection Acts.
                  </Typography>
                  <Typography className={classes.mb4}>
                    Kind regards,
                  </Typography>
                  <Typography>
                    {TPOName}
                  </Typography>
                </Paper>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isAgreed}
                      onChange={e => setAgreed(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box fontSize={18}>
                      I acknowledge that the information provided is correct.
                      <Box color={'red'} component={'span'} ml={0.5}>
                        *
                      </Box>
                    </Box>
                  }
                />
              </Box>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <SignatureCanvas
                  penColor="green"
                  canvasProps={{ className: classes.sigCanvas }}
                  ref={sigCanvas}
                  onEnd={() => setSigned(true)}
                />
                <Box
                  display={'flex'}
                  justifyContent={'space-between'}
                  width="100%"
                >
                  <Button
                    testId={'sign'}
                    size="small"
                    onClick={() => {
                      sigCanvas.current.clear();
                      setSigned(false);
                    }}
                  >
                    Clear
                  </Button>
                  <Box color={'blue'}>{formatDate(new Date())}</Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Grid item xs={6} style={{textAlign: 'right'}}>
              <Button
                disabled={!(isSigned && isAgreed)}
                variant={'contained'}
                color={'primary'}
                testId={'confirm'}
                onClick={() => {
                  handleSign();
                }}
                style={{
                  marginTop: 16,
                }}
              >
                Confirm
              </Button>
            </Grid>
          </Grid>
        </Grid>

      </>}
      <PDFPreview
        open={store.isPdfPreviewModalOpen}
        title={store.pdfTitle}
        data={store.pdfData}
        headers={{}}
        fileName={store.pdfTitle+'.pdf'}
        onClose={()=>{
          store.pdfTitle = null;
          store.pdfData = null;
          store.isPdfPreviewModalOpen = false;
        }}
      />
    </>
  )
})